import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../auth";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { PortalHeader } from "../components/PortalHeader";

export default function PrintQueuePage() {
  const { API, authHeaders, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [shipping, setShipping] = useState(null);
  const [tracking, setTracking] = useState({ tracking_number: "", carrier: "" });

  const load = () => axios.get(`${API}/admin/memory-book-orders`, { headers: authHeaders }).then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const markShipped = async (oid) => {
    try {
      await axios.post(`${API}/admin/memory-book-orders/${oid}/mark-shipped`, tracking, { headers: authHeaders });
      toast.success("Marked as shipped");
      setShipping(null); setTracking({ tracking_number: "", carrier: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const paid = orders.filter((o) => o.status === "paid");
  const shippedOrders = orders.filter((o) => o.status === "shipped");

  return (
    <div className="min-h-screen" data-testid="print-queue-page">
      <PortalHeader eyebrow="Print queue" backTo="/admin" />
      <div className="px-6 py-10 max-w-4xl mx-auto">
      <h1 className="font-display text-4xl">Print queue</h1>
      <hr className="tcm-gold-rule my-6" />

      <section className="mb-10">
        <p className="tcm-eyebrow mb-3">Ready to print ({paid.length})</p>
        {paid.length === 0 && <p className="text-sm text-[color:var(--tcm-navy-70)]">Nothing paid and awaiting dispatch.</p>}
        <div className="space-y-4">
          {paid.map((o) => (
            <div key={o.id} className="tcm-double-frame" data-testid={`order-${o.id}`}>
              <div className="tcm-inner p-5">
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <p className="tcm-eyebrow">{o.trip?.destination} · {o.client?.full_name}</p>
                    <p className="font-display text-2xl mt-1">Memory book — {o.tier}</p>
                    <p className="text-sm mt-1">{o.currency} {o.amount.toFixed(2)}</p>
                  </div>
                  <a data-testid={`dl-pdf-${o.id}`} href={`${API}/admin/memory-book-orders/${o.id}/pdf?auth=${token}`}
                     target="_blank" rel="noreferrer" className="tcm-btn-outline inline-flex items-center gap-2">
                    <FileDown size={12} /> PDF
                  </a>
                </div>
                <div className="mt-4 border-t border-[color:var(--tcm-navy-15)] pt-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="tcm-eyebrow mb-1">Ship to</p>
                    <p className="text-sm whitespace-pre-line">
                      {[o.address?.full_name, o.address?.line1, o.address?.line2, o.address?.city, o.address?.postcode, o.address?.country].filter(Boolean).join("\n")}
                    </p>
                    {o.address?.phone && <p className="tcm-eyebrow mt-2">{o.address.phone}</p>}
                  </div>
                  <div>
                    {shipping === o.id ? (
                      <div className="space-y-2">
                        <input data-testid={`tracking-${o.id}`} className="tcm-input" placeholder="Tracking number"
                               value={tracking.tracking_number} onChange={(e) => setTracking({ ...tracking, tracking_number: e.target.value })} />
                        <input className="tcm-input" placeholder="Carrier (e.g. Royal Mail)"
                               value={tracking.carrier} onChange={(e) => setTracking({ ...tracking, carrier: e.target.value })} />
                        <div className="flex gap-2">
                          <button data-testid={`confirm-ship-${o.id}`} onClick={() => markShipped(o.id)} className="tcm-btn-primary flex-1">Confirm</button>
                          <button onClick={() => setShipping(null)} className="tcm-btn-outline">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button data-testid={`ship-${o.id}`} onClick={() => setShipping(o.id)} className="tcm-btn-primary w-full">
                        Mark as shipped
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {shippedOrders.length > 0 && (
        <section>
          <p className="tcm-eyebrow mb-3">Recently shipped</p>
          <div className="space-y-3">
            {shippedOrders.map((o) => (
              <div key={o.id} className="border border-[#1E2D50] p-4">
                <p className="tcm-eyebrow">{o.trip?.destination} · {o.client?.full_name}</p>
                <div className="flex justify-between mt-1">
                  <p className="font-display text-lg">Memory book — {o.tier}</p>
                  <p className="text-sm">{o.tracking_number || "—"} {o.carrier ? `· ${o.carrier}` : ""}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      </div>
    </div>
  );
}
